/**
 * Video Generator - Video Tab
 * Video generation functionality using Replicate API.
 *
 * Async, browser-independent flow (mirrors splat.js — see issue #1780):
 * generateReplicateVideo creates a generation job (with a Replicate webhook)
 * and returns its jobId immediately, instead of holding the callable
 * connection open for the whole multi-minute render (which Safari drops,
 * losing the result while tokens were still charged). When the job finishes,
 * the webhook saves the video to the user's gallery server-side, so it lands
 * even if this tab was closed. This UI polls getGenerationJobStatus only to
 * reflect progress and show the result while open.
 */

import FluxUI from './main.js';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@shared/services/firebase.js';
import useImageGenStore from './store.js';
import ImageUploadUtils from './image-upload-utils.js';
import { VIDEO_MODELS } from '@shared/constants/replicateModels.js';
import { getDefaultInstructions } from '@shared/constants/renderStyles.js';
import { mountVideoModelSelector } from './mount-video-model-selector.js';
import { syncJobNotifyEmail } from './job-notify.js';
import {
  pollGenerationJob,
  forceJobNotifyEmail
} from '@shared/utils/generationJobs.js';
import { t } from './i18n/messages.js';
import { isTokenExhaustedError } from '@shared/utils/tokens.js';

// Video tab module
const VideoTab = {
  // Tab state
  currentParams: {},
  currentVideoUrl: '',
  selectedAspectRatio: '16:9', // Default aspect ratio
  selectedDuration: 5, // Default duration in seconds (5 or 10)
  selectedModel: 'google/veo-3.1-fast', // Default video model
  imageData: null, // Base64 image data for video generation

  // Timer state
  renderStartTime: null,
  elapsedTime: 0,
  renderProgress: 0,
  timerInterval: null,

  // Job-status poll state (see pollVideoStatus)
  activePoll: null, // { promise, cancel } from pollGenerationJob
  activeJobId: null, // in-flight job — target of the email toggle

  // Overall poll ceiling. Renders run ~1.5–2.5 minutes; 15 min is generous
  // headroom before we give up locally (the job still finishes server-side
  // regardless — the video lands in the gallery either way).
  POLL_MAX_MS: 15 * 60 * 1000,

  // Estimated generation times (in seconds) - built from VIDEO_MODELS
  estimatedTimes: Object.entries(VIDEO_MODELS).reduce((acc, [key, model]) => {
    acc[key] = model.estimatedTime;
    return acc;
  }, {}),

  // DOM Elements
  elements: {},

  // React component instances
  modelSelectorInstance: null,

  // Initialize the tab
  init: function () {
    // Get tab container
    const tabContainer = document.getElementById('video-tab');
    if (!tabContainer) {
      console.error('Video Tab: Container element not found!');
      return;
    }

    // Create the HTML content
    this.createTabContent(tabContainer);

    // Now that content is created, get all the necessary elements
    this.getElements();

    // Mount the React model selector component
    this.mountVideoModelSelectorComponent();

    // Setup event listeners
    this.setupEventListeners();

    // Initialize token cost display and duration labels for default model
    this.updateTokenCostDisplay();

    // Generate an initial random seed on load (commented out - seed doesn't work for video models)
    // this.generateRandomSeed();

    // Register this module with the main UI for updates
    FluxUI.tabModules.video = this;

    // Check for pending gallery item from editor
    this.checkForPendingGalleryItem();
  },

  // Check for pending gallery item from cross-app communication
  checkForPendingGalleryItem: function () {
    try {
      const pendingItemJson = localStorage.getItem('pendingAssetItem');
      if (!pendingItemJson) return;

      const pendingItem = JSON.parse(pendingItemJson);

      // Check if this item is for this tab and is recent (within 10 seconds)
      if (
        pendingItem.targetTab === 'video' &&
        Date.now() - pendingItem.timestamp < 10000
      ) {
        console.log('Loading pending gallery item for video tab:', pendingItem);

        // Load the data URL
        if (
          pendingItem.imageDataUrl &&
          typeof pendingItem.imageDataUrl === 'string'
        ) {
          this.setInputImage(
            pendingItem.imageDataUrl,
            t('video.galleryItem', { id: pendingItem.id })
          );
        }

        // Clear the pending item after loading
        localStorage.removeItem('pendingAssetItem');
      }
    } catch (error) {
      console.error('Failed to load pending gallery item:', error);
      // Clear invalid data
      localStorage.removeItem('pendingAssetItem');
    }
  },

  // Get all DOM elements after content is created
  getElements: function () {
    // Model - React component container
    this.elements.modelSelectorContainer = document.getElementById(
      'video-model-selector-container'
    );

    // Prompt
    this.elements.promptInput = document.getElementById('video-prompt-input');

    // Image upload
    this.elements.imageInput = document.getElementById('video-image-input');
    this.elements.imageName = document.getElementById('video-image-name');
    this.elements.imageUploadLabel = document.getElementById(
      'video-image-upload-label'
    );
    this.elements.imagePreviewContainer = document.getElementById(
      'video-image-preview-container'
    );
    this.elements.imagePreview = document.getElementById('video-image-preview');
    this.elements.imageClear = document.getElementById('video-image-clear');

    // Aspect Ratio
    this.elements.aspectRatioSelector = document.getElementById(
      'video-aspect-ratio-selector'
    );

    // Duration
    this.elements.duration5sRadio =
      document.getElementById('video-duration-5s');
    this.elements.duration10sRadio =
      document.getElementById('video-duration-10s');

    // Parameters (commented out - seed doesn't work for video models)
    // this.elements.seedInput = document.getElementById('video-seed-input');
    // this.elements.randomSeedBtn = document.getElementById(
    //   'video-random-seed-btn'
    // );
    // this.elements.randomizeSeedCheckbox = document.getElementById(
    //   'video-randomize-seed-checkbox'
    // );

    // Advanced options (commented out - seed doesn't work for video models)
    // this.elements.advancedToggle = document.getElementById(
    //   'video-advanced-toggle'
    // );
    // this.elements.advancedOptions = document.getElementById(
    //   'video-advanced-options'
    // );
    // this.elements.advancedIcon = document.getElementById('video-advanced-icon');

    // Preview
    this.elements.previewContainer = document.getElementById(
      'video-preview-container'
    );
    this.elements.previewVideo = document.getElementById('video-preview-video');
    this.elements.generationPlaceholder = document.getElementById(
      'video-generation-placeholder'
    );
    this.elements.loadingIndicator = document.getElementById(
      'video-loading-indicator'
    );
    this.elements.loadingText = document.getElementById('video-loading-text');
    this.elements.progressBar = document.getElementById('video-progress-bar');
    this.elements.overtimeText = document.getElementById('video-overtime-text');

    // Action buttons
    this.elements.actionButtons = document.getElementById(
      'video-action-buttons'
    );
    this.elements.copyParamsBtn = document.getElementById(
      'video-copy-params-btn'
    );
    this.elements.openVideoBtn = document.getElementById(
      'video-open-video-btn'
    );
    this.elements.downloadVideoBtn = document.getElementById(
      'video-download-video-btn'
    );
    this.elements.copyVideoUrlBtn = document.getElementById(
      'video-copy-video-url-btn'
    );

    // Generate button
    this.elements.generateBtn = document.getElementById('video-generate-btn');
    this.elements.generateSpinner = document.getElementById(
      'video-generate-spinner'
    );
    this.elements.generateText = document.getElementById('video-generate-text');
    this.elements.tokenCostDisplay =
      document.getElementById('video-token-cost');
    this.elements.notifyEmail = document.getElementById('video-notify-email');
    this.elements.notifyEmailRow = document.getElementById(
      'video-notify-email-row'
    );

    // Verify critical elements
    let missingElements = [];
    ['modelSelectorContainer', 'promptInput', 'generateBtn'].forEach((elem) => {
      if (!this.elements[elem]) {
        missingElements.push(elem);
      }
    });

    if (missingElements.length > 0) {
      console.error('Video Tab: Critical elements not found:', missingElements);
    }
  },

  // Mount the VideoModelSelector React component
  mountVideoModelSelectorComponent: function () {
    const container = this.elements.modelSelectorContainer;
    if (!container) {
      console.error('Video model selector container not found');
      return;
    }

    this.modelSelectorInstance = mountVideoModelSelector(container, {
      value: this.selectedModel,
      onChange: (modelId) => {
        this.selectedModel = modelId;
        this.updateTokenCostDisplay();
        // Re-render the component with the new value
        if (this.modelSelectorInstance) {
          this.modelSelectorInstance.update({ value: modelId });
        }
      },
      disabled: false
    });
  },

  // Create the tab content HTML
  createTabContent: function (container) {
    container.innerHTML = `
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <!-- Parameters Column -->
                <div class="lg:col-span-1 bg-white rounded-lg shadow p-6">
                    <h2 class="text-lg font-medium mb-1">${t('video.videoGenerationSettings')}</h2>
                    <p class="text-sm text-gray-500 mb-4">${t('video.videoGenerationSettingsDescription')}</p>

                    <!-- Model Selection -->
                    <div class="mb-4">
                        <label class="block text-sm font-medium text-gray-700 mb-1">${t('video.model')}</label>
                        <div id="video-model-selector-container"></div>
                    </div>

                    <!-- Image Upload (Required) -->
                    <div class="mb-4">
                        <label class="block text-sm font-medium text-gray-700 mb-1">${t('video.sourceImage')} <span class="text-red-500">*</span></label>
                        <div class="flex flex-col space-y-2">
                            <label id="video-image-upload-label" class="flex items-center justify-center w-full h-20 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                                <div class="flex flex-col items-center">
                                    <p class="text-sm text-gray-500">${t('video.clickToUploadImage')}</p>
                                    <p id="video-image-name" class="text-xs text-gray-400 mt-1">${t('video.noFileSelected')}</p>
                                </div>
                                <input id="video-image-input" type="file" class="hidden" accept="image/png, image/jpeg, image/jpg" />
                            </label>
                            <div id="video-image-preview-container" class="hidden relative">
                                <img id="video-image-preview" class="w-full rounded-lg border border-gray-300" alt="${t('video.selectedImageAlt')}">
                                <button id="video-image-clear" class="absolute top-2 right-2 p-1 bg-white bg-opacity-80 rounded-full hover:bg-opacity-100 hover:bg-red-50 shadow hover:shadow-lg transition-all duration-200" title="${t('video.clearImage')}">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-600 hover:text-red-600 transition-colors duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Prompt (Optional) -->
                    <div class="mb-4">
                        <label class="block text-sm font-medium text-gray-700 mb-1">${t('video.promptOptional')}</label>
                        <textarea id="video-prompt-input" rows="3" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                  placeholder="${getDefaultInstructions('video')}"></textarea>
                    </div>

                    <!-- Aspect Ratio -->
                    <div class="mb-4">
                        <label class="block text-sm font-medium text-gray-700 mb-1">${t('video.aspectRatio')}</label>
                        <select id="video-aspect-ratio-selector" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                            <option value="16:9" selected>${t('video.aspectRatio169Landscape')}</option>
                            <option value="9:16">${t('video.aspectRatio916Portrait')}</option>
                            <option value="1:1">${t('video.aspectRatio11Square')}</option>
                            <option value="4:3">4:3</option>
                            <option value="3:4">3:4</option>
                        </select>
                    </div>

                    <!-- Duration -->
                    <div class="mb-4">
                        <label class="block text-sm font-medium text-gray-700 mb-2">${t('video.duration')}</label>
                        <div class="space-y-2">
                            <div class="flex items-center">
                                <input type="radio" id="video-duration-5s" name="video-duration" value="5" checked class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300">
                                <label for="video-duration-5s" class="ml-2 block text-sm text-gray-700">${t('video.duration5Seconds10Tokens')}</label>
                            </div>
                            <div class="flex items-center">
                                <input type="radio" id="video-duration-10s" name="video-duration" value="10" class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300">
                                <label for="video-duration-10s" class="ml-2 block text-sm text-gray-700">${t('video.duration10Seconds20Tokens')}</label>
                            </div>
                        </div>
                    </div>

                    <!-- Advanced Options (Hidden - seed doesn't work for video models) -->
                    <!-- <div class="mb-4">
                        <div class="flex justify-between items-center cursor-pointer" id="video-advanced-toggle">
                            <span class="text-sm font-medium text-gray-700">Advanced Options</span>
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" id="video-advanced-icon">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>

                        <div class="mt-2 hidden" id="video-advanced-options">
                            <div class="mb-3 param-group">
                                <label class="block text-sm font-medium text-gray-700 mb-1">Seed</label>
                                <div class="flex">
                                    <input type="number" id="video-seed-input" placeholder="Random" class="w-full px-3 py-2 border border-gray-300 rounded-l-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                                    <button id="video-random-seed-btn" class="px-3 py-2 bg-gray-100 border border-gray-300 border-l-0 rounded-r-md hover:bg-gray-200">
                                        🎲
                                    </button>
                                </div>
                                <div class="mt-2 flex items-center">
                                    <input type="checkbox" id="video-randomize-seed-checkbox" class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked>
                                    <label for="video-randomize-seed-checkbox" class="ml-2 block text-sm text-gray-700">Randomize seed before each generation</label>
                                </div>
                            </div>
                        </div>
                    </div> -->

                    <!-- Generate Button -->
                    <button id="video-generate-btn" class="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center justify-center gap-2">
                        <svg id="video-generate-spinner" class="hidden animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span id="video-generate-text">${t('video.generateVideo')}</span>
                        <span class="inline-flex items-center rounded" style="background: rgba(0, 0, 0, 0.15); padding: 6px 8px; gap: 4px;">
                            <img src="/ui_assets/token-image.png" alt="${t('video.tokenAlt')}" class="w-5 h-5" />
                            <span id="video-token-cost" class="text-sm font-medium">10</span>
                        </span>
                    </button>

                    <!-- Email when done. Hidden until a job is in flight —
                         mid-render it's the "you can close this tab"
                         affordance; toggling writes through to the job doc
                         (setGenerationJobNotify). Defaults ON: renders are
                         usually ~2 minutes but queue waits can stretch much
                         longer. Suppressed server-side if this tab is still
                         open when it finishes (same as the splat tab). -->
                    <label id="video-notify-email-row" class="hidden flex items-center gap-2 mt-3 text-sm text-gray-600 cursor-pointer select-none">
                        <input id="video-notify-email" type="checkbox" checked
                            class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                        ${t('video.emailWhenReady')}
                    </label>
                </div>

                <!-- Preview Column -->
                <div class="lg:col-span-2 bg-white rounded-lg shadow">
                    <div class="p-6 border-b border-gray-200">
                        <h2 class="text-lg font-medium">${t('video.preview')}</h2>
                    </div>
                    <div class="p-6 flex flex-col items-center justify-center min-h-[500px]" id="video-preview-container">
                        <div id="video-generation-placeholder" class="text-center text-gray-400">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            <p>${t('video.generatedVideoWillAppearHere')}</p>
                        </div>
                        <video id="video-preview-video" class="max-w-full max-h-[500px] hidden rounded-lg shadow-lg" controls></video>
                        <div id="video-loading-indicator" class="hidden flex flex-col items-center w-full max-w-md">
                            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
                            <div class="video-rendering-content">
                                <div class="video-progress-container">
                                    <div class="video-progress-bar" id="video-progress-bar" style="width: 0%;"></div>
                                    <div class="video-progress-stripes"></div>
                                </div>
                                <span class="video-progress-text" id="video-loading-text">0s/40s</span>
                                <span class="video-progress-text hidden" id="video-overtime-text" style="margin-top: 4px; color: #fbbf24;">${t('video.generationTakingLonger')}</span>
                            </div>
                        </div>
                    </div>
                    <div class="px-6 pb-6">
                        <div class="flex flex-wrap justify-center gap-2 mt-4" id="video-action-buttons">
                            <button id="video-copy-params-btn" class="px-3 py-1.5 border border-gray-300 bg-white text-gray-600 rounded-md text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 hidden">
                                ${t('video.copyParameters')}
                            </button>
                            <button id="video-open-video-btn" class="px-3 py-1.5 border border-gray-300 bg-white text-gray-600 rounded-md text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 hidden">
                                ${t('video.openVideo')}
                            </button>
                            <button id="video-download-video-btn" class="px-3 py-1.5 border border-gray-300 bg-white text-gray-600 rounded-md text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 hidden">
                                ${t('video.downloadVideo')}
                            </button>
                            <button id="video-copy-video-url-btn" class="px-3 py-1.5 border border-gray-300 bg-white text-gray-600 rounded-md text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 hidden">
                                ${t('video.copyVideoUrl')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `;
  },

  // Setup event listeners
  setupEventListeners: function () {
    if (!this.elements.generateBtn) {
      console.error(
        'Video Tab: Cannot set up event listeners, elements not found'
      );
      return;
    }

    // Mid-render email opt-in writes through to the in-flight job doc
    this.elements.notifyEmail?.addEventListener('change', () => {
      syncJobNotifyEmail(this.activeJobId, this.elements.notifyEmail);
    });

    // Image upload
    this.elements.imageInput.addEventListener(
      'change',
      this.handleImageUpload.bind(this)
    );

    // Setup drag and drop for image
    ImageUploadUtils.setupDragAndDrop(
      this.elements.imageUploadLabel,
      this.elements.imageInput,
      (dataUrl, fileName) => {
        this.elements.imageName.textContent = fileName;
        // Store base64 data (without the data URL prefix)
        this.imageData = dataUrl.split(',')[1];
        // Show preview
        this.showImagePreview(dataUrl);
      }
    );

    // Clear image button
    if (this.elements.imageClear) {
      this.elements.imageClear.addEventListener(
        'click',
        this.clearImage.bind(this)
      );
    }

    // Duration radio buttons - update token cost display
    this.elements.duration5sRadio.addEventListener('change', () => {
      this.updateTokenCostDisplay();
    });
    this.elements.duration10sRadio.addEventListener('change', () => {
      this.updateTokenCostDisplay();
    });

    // Advanced toggle (commented out - seed doesn't work for video models)
    // this.elements.advancedToggle.addEventListener(
    //   'click',
    //   this.toggleAdvancedOptions.bind(this)
    // );

    // Random seed button (commented out - seed doesn't work for video models)
    // this.elements.randomSeedBtn.addEventListener(
    //   'click',
    //   this.generateRandomSeed.bind(this)
    // );

    // Generate button
    this.elements.generateBtn.addEventListener(
      'click',
      this.generateVideo.bind(this)
    );

    // Keyboard shortcut for prompt input (Cmd+Enter on Mac, Ctrl+Enter on PC)
    this.elements.promptInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        this.elements.generateBtn.click();
      }
    });

    // Setup action buttons
    if (this.elements.openVideoBtn) {
      this.elements.openVideoBtn.addEventListener(
        'click',
        this.openVideo.bind(this)
      );
    }

    if (this.elements.downloadVideoBtn) {
      this.elements.downloadVideoBtn.addEventListener(
        'click',
        this.downloadVideo.bind(this)
      );
    }

    if (this.elements.copyVideoUrlBtn) {
      this.elements.copyVideoUrlBtn.addEventListener(
        'click',
        this.copyVideoUrl.bind(this)
      );
    }

    if (this.elements.copyParamsBtn) {
      this.elements.copyParamsBtn.addEventListener(
        'click',
        this.copyParams.bind(this)
      );
    }
  },

  // Toggle advanced options visibility
  toggleAdvancedOptions: function () {
    this.elements.advancedOptions.classList.toggle('hidden');
    const isVisible =
      !this.elements.advancedOptions.classList.contains('hidden');
    if (isVisible) {
      this.elements.advancedIcon.innerHTML = `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />`;
    } else {
      this.elements.advancedIcon.innerHTML = `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />`;
    }
  },

  // Update token cost display based on selected duration and model
  updateTokenCostDisplay: function () {
    const duration = this.getSelectedDuration();
    const modelConfig = VIDEO_MODELS[this.selectedModel];
    const tokenCost =
      duration === 10
        ? modelConfig?.tokenCost10s || 20
        : modelConfig?.tokenCost5s || 10;
    if (this.elements.tokenCostDisplay) {
      this.elements.tokenCostDisplay.textContent = tokenCost;
    }
    // Update duration radio button labels with per-model costs
    this.updateDurationLabels();
  },

  // Update duration radio button labels to show per-model token costs
  updateDurationLabels: function () {
    const modelConfig = VIDEO_MODELS[this.selectedModel];
    const cost5s = modelConfig?.tokenCost5s || 10;
    const cost10s = modelConfig?.tokenCost10s || 20;
    const label5s = document.querySelector('label[for="video-duration-5s"]');
    const label10s = document.querySelector('label[for="video-duration-10s"]');
    // Veo models only support 4s/8s durations
    const isVeo = modelConfig?.shortDuration === 4;
    const shortLabel = isVeo
      ? t('video.duration4Seconds')
      : t('video.duration5Seconds');
    const longLabel = isVeo
      ? t('video.duration8Seconds')
      : t('video.duration10Seconds');
    if (label5s) {
      label5s.textContent = t('video.durationTokensLabel', {
        duration: shortLabel,
        cost: cost5s
      });
    }
    if (label10s) {
      label10s.textContent = t('video.durationTokensLabel', {
        duration: longLabel,
        cost: cost10s
      });
    }
  },

  // Get selected duration
  getSelectedDuration: function () {
    if (this.elements.duration10sRadio.checked) {
      return 10;
    }
    return 5; // Default to 5 seconds
  },

  // Generate a random seed
  generateRandomSeed: function () {
    this.elements.seedInput.value = Math.floor(Math.random() * 1000000);
  },

  // Generate a video
  generateVideo: function () {
    // Check if user is authenticated
    if (!window.authState || !window.authState.isAuthenticated) {
      useImageGenStore.getState().setModal('signin');
      return;
    }

    // Check if user has tokens
    const hasTokens = window.authState.tokenProfile?.genToken > 0;
    if (!hasTokens) {
      // Show purchase modal
      window.dispatchEvent(
        new CustomEvent('openPurchaseModal', {
          detail: { tokenType: 'genToken' }
        })
      );
      return;
    }

    // Build parameters
    const params = this.buildRequestParams();

    if (!params) {
      return;
    }

    // Store current parameters for later use
    this.currentParams = params;

    // Show loading state
    this.stopPolling();
    this.toggleLoading(true);

    // Submit the job. The callable returns { success, jobId } immediately —
    // no multi-minute open connection for Safari to drop (#1780). Completion
    // is handled server-side (webhook saves to the gallery); we poll only to
    // drive this tab's live UI.
    const generateReplicateVideo = httpsCallable(
      functions,
      'generateReplicateVideo'
    );

    generateReplicateVideo(params)
      .then((result) => {
        if (!result.data || !result.data.success || !result.data.jobId) {
          throw new Error(
            result.data?.message || t('video.couldNotStartGeneration')
          );
        }

        // Tokens are charged at submit (refunded on failure); reflect that.
        window.dispatchEvent(new CustomEvent('tokenCountChanged'));

        // The job is now really in flight — reveal the email opt-in (check
        // it, close the tab, get the result by email).
        this.activeJobId = result.data.jobId;
        this.elements.notifyEmailRow?.classList.remove('hidden');

        // The job now shows as a pending card in the assets gallery, driven by
        // the live Firestore listener on the job doc — it persists across
        // reloads and tabs without any client state here.
        this.pollVideoStatus(result.data.jobId);
      })
      .catch((error) => {
        console.error('Video generation error:', error);
        let message = t('video.failedToGenerate');

        if (error.code === 'unauthenticated') {
          message = t('video.pleaseSignIn');
        } else if (isTokenExhaustedError(error)) {
          message = t('video.insufficientTokens');
        } else if (error.code === 'permission-denied') {
          message = t('video.noPermission');
        } else if (error.code === 'unavailable') {
          message = t('video.serviceUnavailable');
        } else if (error.message) {
          message = error.message;
        }

        FluxUI.showNotification(message, 'error');
        this.toggleLoading(false);
      });
  },

  // Drive the live UI off the shared getGenerationJobStatus poll until the
  // job is terminal. Any non-terminal status (queued|running|saving) just
  // keeps polling.
  pollVideoStatus: function (jobId) {
    this.stopPolling();
    this.activePoll = pollGenerationJob(jobId, {
      resultField: 'video_url',
      maxMs: this.POLL_MAX_MS
    });
    this.activePoll.promise
      .then((data) => {
        if (!data) return; // cancelled — a newer submit or teardown took over

        // The video was saved to the gallery server-side (works even if this
        // tab had been closed). Just show it and refresh the gallery island so
        // the pending-job card hands its slot to the real asset.
        this.displayVideo(data.video_url);
        this.toggleLoading(false);
        window.dispatchEvent(new Event('assets:refresh'));
        window.dispatchEvent(new CustomEvent('tokenCountChanged'));
        FluxUI.showNotification(t('video.generatedAndSaved'), 'success');
      })
      .catch(async (error) => {
        if (error.timedOut) {
          // The job outlived the poll window — unusual enough that the user
          // shouldn't have to babysit the tab: force the completion email on.
          const forced = await forceJobNotifyEmail(jobId);
          if (this.elements.notifyEmail) {
            this.elements.notifyEmail.checked = true;
          }
          this.toggleLoading(false);
          FluxUI.showNotification(
            forced
              ? t('video.takingLongerEmailForced')
              : t('video.takingLongerCheckGallery'),
            forced ? 'warning' : 'error'
          );
          return;
        }
        // failed/canceled — the server refunds on failure; refresh the balance.
        window.dispatchEvent(new CustomEvent('tokenCountChanged'));
        this.toggleLoading(false);
        FluxUI.showNotification(
          error.jobError
            ? t('video.generationFailedWithError', { error: error.jobError })
            : t('video.generationFailedRefunded'),
          'error'
        );
      });
  },

  stopPolling: function () {
    if (this.activePoll) {
      this.activePoll.cancel();
      this.activePoll = null;
    }
  },

  // Build request parameters
  buildRequestParams: function () {
    const params = {};

    // Add model name
    params.model_name = this.selectedModel;

    // Check if image is uploaded (required)
    if (!this.imageData) {
      FluxUI.showNotification(t('video.pleaseUploadImage'), 'error');
      return null;
    }
    params.input_image = this.imageData;

    // Add prompt (optional with default)
    const prompt = this.elements.promptInput.value.trim();
    params.prompt = prompt || getDefaultInstructions('video');

    // Add aspect ratio
    params.aspect_ratio = this.elements.aspectRatioSelector.value;

    // Add duration (5 or 10 seconds)
    params.duration_seconds = this.getSelectedDuration();

    // Opt-in completion email, recorded on the job doc. The server only sends
    // it if this tab isn't around to ack the result (i.e. closed).
    params.notify = { email: !!this.elements.notifyEmail?.checked };

    // Seed functionality removed - doesn't work for video models
    // Check if seed should be randomized before generation
    // if (this.elements.randomizeSeedCheckbox.checked) {
    //   this.generateRandomSeed();
    // }

    // Add seed if provided
    // if (this.elements.seedInput.value) {
    //   params.seed = parseInt(this.elements.seedInput.value);
    // }

    return params;
  },

  // Display the generated video
  displayVideo: function (videoUrl) {
    // Store the URL
    this.currentVideoUrl = videoUrl;

    // Show video
    this.elements.previewVideo.src = videoUrl;
    this.elements.previewVideo.classList.remove('hidden');
    this.elements.generationPlaceholder.classList.add('hidden');

    // Autoplay the video once it's loaded
    this.elements.previewVideo.load();
    this.elements.previewVideo.play().catch((error) => {
      // Autoplay might be blocked by browser, that's okay
      console.log('Autoplay prevented:', error);
    });

    // Show action buttons
    this.elements.copyParamsBtn.classList.remove('hidden');
    this.elements.openVideoBtn.classList.remove('hidden');
    this.elements.downloadVideoBtn.classList.remove('hidden');
    this.elements.copyVideoUrlBtn.classList.remove('hidden');
  },

  // Start the timer
  startTimer: function (modelName) {
    this.renderStartTime = Date.now();
    this.elapsedTime = 0;
    this.updateTimerDisplay();

    // Update timer every second
    this.timerInterval = setInterval(() => {
      const elapsed = Math.round((Date.now() - this.renderStartTime) / 1000);
      this.elapsedTime = elapsed;
      this.updateTimerDisplay();
    }, 1000);
  },

  // Stop the timer
  stopTimer: function () {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    this.renderStartTime = null;
    this.elapsedTime = 0;
    this.renderProgress = 0;

    // Reset progress bar
    if (this.elements.progressBar) {
      this.elements.progressBar.style.width = '0%';
    }

    // Hide overtime warning
    if (this.elements.overtimeText) {
      this.elements.overtimeText.classList.add('hidden');
    }
  },

  // Update timer display
  updateTimerDisplay: function () {
    const modelName = this.selectedModel;
    const estimatedTime = this.estimatedTimes[modelName] || 40;

    // Calculate progress percentage
    this.renderProgress = Math.min(
      (this.elapsedTime / estimatedTime) * 100,
      100
    );

    // Update progress bar width
    if (this.elements.progressBar) {
      this.elements.progressBar.style.width = `${this.renderProgress}%`;
    }

    // Update text (always show in seconds format)
    this.elements.loadingText.textContent = `${this.elapsedTime}s/${estimatedTime}s`;

    // Show overtime warning if elapsed time is more than 10s over estimate
    if (this.elements.overtimeText) {
      if (this.elapsedTime > estimatedTime + 10) {
        this.elements.overtimeText.classList.remove('hidden');
      } else {
        this.elements.overtimeText.classList.add('hidden');
      }
    }
  },

  // Toggle loading state
  toggleLoading: function (isLoading) {
    if (isLoading) {
      this.elements.loadingIndicator.classList.remove('hidden');
      this.elements.generationPlaceholder.classList.add('hidden');
      this.elements.previewVideo.classList.add('hidden');
      this.elements.generateBtn.disabled = true;
      this.elements.generateBtn.classList.add(
        'opacity-50',
        'cursor-not-allowed'
      );

      // Show spinner, update button text
      if (this.elements.generateSpinner) {
        this.elements.generateSpinner.classList.remove('hidden');
      }
      if (this.elements.generateText) {
        this.elements.generateText.textContent = t('video.generating');
      }

      // Hide action buttons
      this.elements.copyParamsBtn.classList.add('hidden');
      this.elements.openVideoBtn.classList.add('hidden');
      this.elements.downloadVideoBtn.classList.add('hidden');
      this.elements.copyVideoUrlBtn.classList.add('hidden');

      // Start the timer
      const modelName = this.selectedModel;
      this.startTimer(modelName);
    } else {
      // Terminal (or reset): the email toggle only applies to an in-flight
      // job, so it leaves with the loading state.
      this.activeJobId = null;
      this.elements.notifyEmailRow?.classList.add('hidden');
      this.elements.loadingIndicator.classList.add('hidden');
      this.elements.generateBtn.disabled = false;
      this.elements.generateBtn.classList.remove(
        'opacity-50',
        'cursor-not-allowed'
      );

      // Hide spinner, restore button text
      if (this.elements.generateSpinner) {
        this.elements.generateSpinner.classList.add('hidden');
      }
      if (this.elements.generateText) {
        this.elements.generateText.textContent = t('video.generateVideo');
      }

      // Stop the timer
      this.stopTimer();
    }
  },

  // Open the video in a new tab
  openVideo: function () {
    if (!this.currentVideoUrl) {
      FluxUI.showNotification(t('video.noVideoToOpen'), 'error');
      return;
    }
    window.open(this.currentVideoUrl, '_blank');
    FluxUI.showNotification(t('video.videoOpened'), 'success');
  },

  // Download the video
  downloadVideo: function () {
    if (!this.currentVideoUrl) {
      FluxUI.showNotification(t('video.noVideoToDownload'), 'error');
      return;
    }

    // Create download link
    const downloadLink = document.createElement('a');
    downloadLink.href = this.currentVideoUrl;

    // Generate a filename based on the timestamp
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, '-')
      .slice(0, 19);
    const filename = `3dstreet-video-${timestamp}.mp4`;

    downloadLink.download = filename;

    // Append to body, click and remove
    document.body.appendChild(downloadLink);
    downloadLink.click();

    setTimeout(() => {
      document.body.removeChild(downloadLink);
    }, 100);

    FluxUI.showNotification(t('video.videoDownloadStarted'), 'success');
  },

  // Copy the video URL to clipboard
  copyVideoUrl: function () {
    if (!this.currentVideoUrl) {
      FluxUI.showNotification(t('video.noVideoUrlToCopy'), 'error');
      return;
    }
    navigator.clipboard
      .writeText(this.currentVideoUrl)
      .then(() => {
        FluxUI.showNotification(t('video.videoUrlCopied'), 'success');
      })
      .catch((err) => {
        FluxUI.showNotification(
          t('video.failedToCopyUrl', { error: err.message }),
          'error'
        );
      });
  },

  // Copy parameters to clipboard
  copyParams: function () {
    if (Object.keys(this.currentParams).length === 0) {
      FluxUI.showNotification(t('video.noParametersToCopy'), 'error');
      return;
    }

    // Format params as JSON string with indentation
    const paramsString = JSON.stringify(this.currentParams, null, 2);
    navigator.clipboard
      .writeText(paramsString)
      .then(() => {
        FluxUI.showNotification(t('video.parametersCopied'), 'success');
      })
      .catch((err) => {
        FluxUI.showNotification(
          t('video.failedToCopyParameters', { error: err.message }),
          'error'
        );
      });
  },

  // Handle image file upload
  handleImageUpload: function (e) {
    const file = e.target.files[0];
    if (!file) return;

    this.elements.imageName.textContent = file.name;

    const reader = new FileReader();
    reader.onload = (event) => {
      // Store base64 data (without the data URL prefix)
      this.imageData = event.target.result.split(',')[1];

      // Show preview
      this.showImagePreview(event.target.result);
    };
    reader.readAsDataURL(file);
  },

  // Show image preview
  showImagePreview: function (imageDataUrl) {
    if (
      !this.elements.imagePreview ||
      !this.elements.imagePreviewContainer ||
      !this.elements.imageUploadLabel
    ) {
      return;
    }

    this.elements.imagePreview.src = imageDataUrl;
    this.elements.imagePreviewContainer.classList.remove('hidden');
    this.elements.imageUploadLabel.classList.add('hidden');
  },

  // Set input image from gallery or other source
  setInputImage: function (imageDataUrl, imageName = t('video.fromGallery')) {
    this.imageData = imageDataUrl.split(',')[1];
    this.elements.imageName.textContent = imageName;

    // Show preview
    this.showImagePreview(imageDataUrl);
  },

  // Clear image
  clearImage: function () {
    this.imageData = null;
    this.elements.imageName.textContent = t('video.noFileSelected');
    this.elements.imagePreview.src = '';
    this.elements.imagePreviewContainer.classList.add('hidden');
    this.elements.imageUploadLabel.classList.remove('hidden');
    this.elements.imageInput.value = '';
  }

  // NOTE: the old client-side saveToGallery was removed — the video is now
  // persisted to the gallery server-side by the generation job's terminal
  // processor (public/functions/replicate.js:saveVideoToGallery), so it saves
  // even if this tab is closed mid-render (#1780).
};

export default VideoTab;
